import { useState } from 'react'
import { Headphones, RefreshCw, Edit, Trash2, ClipboardList } from 'lucide-react'
import { Suporte } from '../tipos/suporte'
import { useDeleteSuporte } from '../hooks/useSuporte'
import { usePagination } from '../../../hooks/usePagination'
import { PaginationControls } from '../../../componentesCompartilhados/paginacao/PaginationControls'
import { formatDateToBrazilian } from '../../../utils/formatUtils'

interface SuporteListProps {
  suportes: Suporte[]
  isLoading: boolean
  onRefresh: () => void
  onEditSuporte: (suporte: Suporte) => void
}

export function SuporteList({ suportes, isLoading, onRefresh, onEditSuporte }: SuporteListProps) {
  const deleteMutation = useDeleteSuporte()
  const [suporteSelecionado, setSuporteSelecionado] = useState<Suporte | null>(null)

  const {
    paginatedItems: suportesPaginados,
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    nextPage,
    previousPage,
  } = usePagination(suportes, {
    totalItems: suportes.length,
    itemsPerPage: 5,
  })

  const handleDelete = (suporte: Suporte) => {
    setSuporteSelecionado(null)
    if (!suporte.id) return
    deleteMutation.mutate(suporte.id)
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-8 text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando suportes...</p>
          <p className="text-sm text-gray-500">Aguarde enquanto buscamos os dados</p>
        </div>
      </div>
    )
  }

  if (!suportes.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-8 text-center">
          <Headphones className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum suporte encontrado</h3>
          <p className="text-gray-600 mb-1">Cadastre um suporte para começar.</p>
          <p className="text-sm text-gray-500">Utilize o botão "Novo Suporte" para iniciar.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
            <Headphones className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Suportes Cadastrados</h3>
            <p className="text-sm text-gray-600">{suportes.length} suporte(s) encontrado(s)</p>
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Suporte
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Responsáveis
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Datas
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {suportesPaginados.map((suporte) => (
              <tr key={suporte.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center font-semibold">
                      <ClipboardList className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{suporte.nome}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Ticket: {suporte.numeroTicket || '-'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{suporte.statusNome || '-'}</div>
                  <div className="text-xs text-gray-500 mt-1">Prioridade: {suporte.prioridadeNome || '-'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">Analista: {suporte.analistaSMNNome || '-'}</div>
                  <div className="text-xs text-gray-500 mt-1">Dev: {suporte.desenvolvedorNome || '-'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    Recebimento:{' '}
                    {formatDateToBrazilian(suporte.dataRecebimento) || '-'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Conclusão:{' '}
                    {formatDateToBrazilian(suporte.dataConclusao || '') || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEditSuporte(suporte)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setSuporteSelecionado(suporte)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={suportes.length}
        startIndex={startIndex}
        endIndex={endIndex}
        onPreviousPage={previousPage}
        onNextPage={nextPage}
        itemLabel="suporte"
      />

      {suporteSelecionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Excluir suporte</h3>
            <p className="text-sm text-gray-600 mb-4">
              Tem certeza que deseja excluir o suporte <strong>{suporteSelecionado.nome}</strong>? Essa ação não poderá
              ser desfeita.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSuporteSelecionado(null)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(suporteSelecionado)}
                className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 transition-all duration-200"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
